import { Popover, Transition } from "@headlessui/react";
import { useAppSelector } from "hooks/hooks";
import { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Avatar from "shared/Avatar/Avatar";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import { socket } from "utils/socket";
import { motion, AnimatePresence } from "framer-motion";
import {
  Bell as BellIcon,
  X as XMarkIcon,
  Clock as ClockIcon,
  Check as CheckIcon,
  Eye as EyeIcon,
  Calendar as CalendarIcon,
  Home as HomeIcon,
  MessageSquare as MessageSquareIcon,
  User as UserIcon,
  Filter as FilterIcon,
} from "lucide-react";

interface NotificationItem {
  id: string;
  message: string;
  operation: string;
  vue: boolean;
  createdAt: string;
  senderId?: string;
  sender?: string;
  senderData?: any;
  locationId?: string;
  prestationId?: string;
}

interface NotifyDropdownProps {
  className?: string;
  open?: boolean;
  notifications: {
    notifications: NotificationItem[];
    unreadCount: number;
    totalNotifications: number;
  };
  loadMoreNotifications: () => void;
  deleteAllNotifications: () => void;
  loading: boolean;
}

const NotifyDropdown: React.FC<NotifyDropdownProps> = ({
  className = "",
  notifications,
  loadMoreNotifications,
  deleteAllNotifications,
  loading,
}) => {
  const [filter, setFilter] = useState<string>("all");
  const [animateCount, setAnimateCount] = useState(false);

  useEffect(() => {
    if (notifications.unreadCount > 0) {
      setAnimateCount(true);
      const timer = setTimeout(() => setAnimateCount(false), 300);
      return () => clearTimeout(timer);
    }
  }, [notifications.unreadCount]);

  const getNotificationIcon = (operation: string) => {
    switch (operation) {
      case "create_reservation":
      case "confirm_reservation":
      case "cancel_reservation":
      case "negate_reservation":
        return <CalendarIcon className="w-4 h-4" />;
      case "confirm_owner_espace":
      case "negate_owner_espace":
      case "create_espace":
        return <HomeIcon className="w-4 h-4" />;
      case "send_message":
        return <MessageSquareIcon className="w-4 h-4" />;
      case "send_avis":
      case "send_presta_avis":
        return <UserIcon className="w-4 h-4" />;
      default:
        return <BellIcon className="w-4 h-4" />;
    }
  };

  const getNotificationColor = (operation: string) => {
    switch (operation) {
      case "create_reservation":
        return "text-orange-500 bg-orange-50 dark:bg-orange-900/20";
      case "confirm_reservation":
        return "text-green-500 bg-green-50 dark:bg-green-900/20";
      case "cancel_reservation":
      case "negate_reservation":
        return "text-red-500 bg-red-50 dark:bg-red-900/20";
      case "confirm_owner_espace":
        return "text-blue-500 bg-blue-50 dark:bg-blue-900/20";
      case "send_message":
        return "text-purple-500 bg-purple-50 dark:bg-purple-900/20";
      default:
        return "text-gray-500 bg-gray-50 dark:bg-gray-900/20";
    }
  };

  const filteredNotifications = notifications.notifications.filter((item: NotificationItem) => {
    if (filter === "all") return true;
    if (filter === "unread") return !item.vue;
    return false;
  });

  const getNotificationLink = (item: NotificationItem): string => {
    const isUserAdmin = userState?.role === "admin";
    const locationLink = "/location/" + item.locationId;
    const prestationLink = "/prestations/" + item.prestationId;
    switch (item.operation) {
      case "create_reservation":
        return "/client/list-pending-reservations";
      case "confirm_reservation":
        return isUserAdmin ? "/admin/dashboard/reservations/confirmed" : `/client/messages`;
      case "cancel_reservation":
        return isUserAdmin ? "/admin/dashboard/reservations/canceled/client" : "/espace-evenementiel";
      case "negate_reservation":
        return isUserAdmin ? "/admin/dashboard/reservations/canceled/owner" : "/espace-evenementiel";
      case "confirm_owner_espace":
        return locationLink;
      case "negate_owner_espace":
        return "/client/locations";
      case "send_avis":
        return `${locationLink}#commentaires`;
      case "send_contat_request":
        return isUserAdmin ? "/admin/dashboard/reclamations" : "";
      case "create_espace":
        return isUserAdmin ? "/admin/dashboard/locations/unapproved" : "";
      case "create_prestation":
        return isUserAdmin ? "/admin/dashboard/prestations" : "";
      case "send_presta_avis":
        return `${prestationLink}#commentaires`;
      case "create_annonce":
        return isUserAdmin ? "/admin/dashboard/annonces/unapproved" : "";
      case "confirm_user_announce":
        return "/client/announces";
      case "block_user_announce":
        return "/client/announces";
      case "send_presta_devis":
        return "/prestataire/pricing";
      case "send_reservation_feedback":
        return isUserAdmin ? "/admin/dashboard/reservations-reclamations" : "/client/transferts-reservations";
      case "send_message":
        return "/client/messages";
      default:
        return "";
    }
  };

  const userState = useAppSelector((state) => state?.auth?.user);
  const user_avatar = "static-files/user_profile_2.png";
  const admin_avatar = "static-files/EVENT TO BEE-17.png";
  const getAvatarUrl = (item: NotificationItem): string => {
    const cloudFrontUrl = process.env.REACT_APP_CLOUD_FRONT || "";
    if (!item.senderId) {
      return cloudFrontUrl + user_avatar;
    }
    if (item?.sender === "ADMIN") {
      return cloudFrontUrl + admin_avatar;
    }
    const clientUrl = item?.senderData?.Clients?.[0]?.client_url?.trim();
    return clientUrl ? cloudFrontUrl + clientUrl : cloudFrontUrl + user_avatar;
  };

  const getSenderName = (item: NotificationItem): string => {
    if (item?.sender !== "ADMIN") {
      return item?.sender || "Utilisateur";
    } else if (item?.sender === "ADMIN") {
      return "Eventtobee";
    } else {
      return "Inconnu";
    }
  };
  const updateNotificationSeen = (item: NotificationItem) => {
    if (userState) {
      socket.emit("update-notification", {
        userId: userState.id,
        notificationId: item.id,
      });
    }
  };

  return (
    <div className={className}>
      <Popover className="relative">
        {({ open, close }) => (
          <>
            <Popover.Button
              className={`${
                open ? "bg-gray-100 dark:bg-neutral-800" : "text-opacity-90"
              } group p-3 hover:bg-gray-100 dark:hover:bg-neutral-800 rounded-full inline-flex items-center text-base font-medium hover:text-opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-opacity-75 relative transition-all duration-200`}
            >
              <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} className="relative">
                <BellIcon className="w-6 h-6 text-gray-600 dark:text-gray-300" />
                {notifications.unreadCount > 0 && (
                  <motion.div
                    animate={{
                      rotate: [0, 10, -10, 10, -10, 0],
                      transition: { duration: 0.5, repeat: 2, repeatDelay: 2 },
                    }}
                    className="absolute inset-0"
                  >
                    <BellIcon className="w-6 h-6 text-blue-500" />
                  </motion.div>
                )}
                {notifications.unreadCount >= 1 && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: animateCount ? 1.2 : 1 }}
                    className="absolute flex items-center justify-center w-5 h-5 bg-gradient-to-r from-red-500 to-pink-500 rounded-full -top-1 -right-1 shadow-lg z-10"
                  >
                    <span className="text-xs font-bold text-white">
                      {notifications.unreadCount > 99 ? "99+" : notifications.unreadCount}
                    </span>
                  </motion.span>
                )}
              </motion.div>
            </Popover.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-300"
              enterFrom="opacity-0 translate-y-1 scale-95"
              enterTo="opacity-100 translate-y-0 scale-100"
              leave="transition ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 scale-100"
              leaveTo="opacity-0 translate-y-1 scale-95"
            >
              <Popover.Panel className="absolute z-20 w-screen max-w-sm px-4 mt-3 -right-4 sm:-right-8 sm:px-0">
                <motion.div
                  initial={{ opacity: 0, scale: 0.9, y: -10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9, y: -10 }}
                  className="overflow-hidden bg-white dark:bg-neutral-800 shadow-2xl rounded-3xl border border-gray-200 dark:border-gray-700 backdrop-blur-xl bg-white/95 dark:bg-neutral-800/95"
                >
                  {/* Header */}
                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-700 border-b border-gray-200 dark:border-gray-600">
                    <div className="flex items-center justify-between px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
                          <BellIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-gray-900 dark:text-white">Notifications</h3>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {notifications.unreadCount} non lue{notifications.unreadCount > 1 ? "s" : ""}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {notifications.unreadCount > 0 && (
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => {
                              // Marquer toutes les notifications comme lues
                              notifications.notifications.forEach((item) => {
                                if (!item.vue) {
                                  updateNotificationSeen(item);
                                }
                              });
                            }}
                            className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 bg-blue-50 dark:bg-blue-900/20 px-3 py-1.5 rounded-full transition-all duration-200"
                          >
                            <CheckIcon className="w-3 h-3 mr-1 inline" />
                            Tout marquer comme lu
                          </motion.button>
                        )}
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => close()}
                          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1"
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </motion.button>
                      </div>
                    </div>

                    {/* Filters */}
                    <div className="px-6 pb-4">
                      <div className="flex space-x-2">
                        {["all", "unread"].map((filterType) => (
                          <motion.button
                            key={filterType}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => setFilter(filterType)}
                            className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all duration-200 ${
                              filter === filterType
                                ? "bg-blue-600 text-white shadow-lg"
                                : "bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600"
                            }`}
                          >
                            <FilterIcon className="w-3 h-3 mr-1 inline" />
                            {filterType === "all" && "Toutes"}
                            {filterType === "unread" && "Non lues"}
                          </motion.button>
                        ))}
                      </div>
                    </div>
                  </div>
                  {/* Notifications List */}
                  <div className="max-h-96 overflow-y-auto bg-gray-50 dark:bg-gray-900/20">
                    {filteredNotifications.length === 0 ? (
                      <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                        <BellIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p className="font-medium">Aucune notification</p>
                        <p className="text-xs mt-1">
                          {filter === "unread" ? "Toutes vos notifications sont lues" : "Vous êtes à jour !"}
                        </p>
                      </div>
                    ) : (
                      <AnimatePresence>
                        {filteredNotifications.map(
                          (item: NotificationItem, index: number) =>
                            item && (
                              <motion.div
                                key={item.id || index}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 20 }}
                                transition={{ delay: index * 0.05 }}
                                className={`relative border-b border-gray-200 dark:border-gray-700 hover:bg-white dark:hover:bg-gray-800 transition-all duration-200 ${
                                  !item.vue ? "bg-blue-50/80 dark:bg-blue-900/20" : ""
                                }`}
                              >
                                <Link
                                  to={getNotificationLink(item)}
                                  className="block p-4"
                                  onClick={() => {
                                    close();
                                    updateNotificationSeen(item);
                                  }}
                                >
                                  <div className="flex items-start space-x-3">
                                    {/* Notification Icon */}
                                    <div
                                      className={`flex-shrink-0 p-2 rounded-xl ${getNotificationColor(item.operation)}`}
                                    >
                                      {getNotificationIcon(item.operation)}
                                    </div>

                                    {/* Avatar */}
                                    <Avatar
                                      sizeClass="w-10 h-10"
                                      imgUrl={getAvatarUrl(item)}
                                      className="flex-shrink-0"
                                    />

                                    {/* Content */}
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center justify-between mb-1">
                                        <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                                          {getSenderName(item)}
                                        </p>
                                        {!item.vue && (
                                          <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 ml-2"></div>
                                        )}
                                      </div>
                                      <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2 mb-2">
                                        {item.message}
                                      </p>
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                                          <ClockIcon className="w-3 h-3 mr-1" />
                                          {new Date(item.createdAt).toLocaleDateString("fr-FR", {
                                            day: "2-digit",
                                            month: "2-digit",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                          })}
                                        </div>
                                        {!item.vue && (
                                          <motion.button
                                            whileHover={{ scale: 1.1 }}
                                            whileTap={{ scale: 0.9 }}
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              updateNotificationSeen(item);
                                            }}
                                            className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 flex items-center"
                                          >
                                            <EyeIcon className="w-3 h-3 mr-1" />
                                            Marquer comme lu
                                          </motion.button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </Link>
                              </motion.div>
                            )
                        )}
                      </AnimatePresence>
                    )}

                    {/* Load More Button */}
                    {filteredNotifications.length > 0 &&
                      notifications.notifications.length < notifications.totalNotifications && (
                        <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={loadMoreNotifications}
                            disabled={loading}
                            className="w-full py-3 text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {loading ? (
                              <div className="flex items-center justify-center">
                                <motion.div
                                  animate={{ rotate: 360 }}
                                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                  className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full mr-2"
                                />
                                Chargement...
                              </div>
                            ) : (
                              "Voir plus de notifications"
                            )}
                          </motion.button>
                        </div>
                      )}

                    {/* Empty State */}
                    {notifications.totalNotifications === 0 && (
                      <div className="p-8 text-center text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800">
                        <motion.div
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.2 }}
                        >
                          <BellIcon className="w-16 h-16 mx-auto mb-4 opacity-30" />
                          <h3 className="text-lg font-medium mb-2">Aucune notification</h3>
                          <p className="text-sm">Vous êtes à jour ! Toutes vos notifications apparaîtront ici.</p>
                        </motion.div>
                      </div>
                    )}
                  </div>
                </motion.div>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
    </div>
  );
};

export default NotifyDropdown;
